import { useRef, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { WardCard } from './WardCard';
import { useWardStore } from '@/stores/wardStore';
import type { WardInfo } from '@/types/ward';

interface WardListProps {
  wards: WardInfo[];
}

export function WardList({ wards }: WardListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const selectedWard = useWardStore((state) => state.selectedWard);

  const virtualizer = useVirtualizer({
    count: wards.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 160, // Increased to prevent any overlapping
    overscan: 5,
  });

  // Auto-scroll to selected ward
  useEffect(() => {
    if (selectedWard && parentRef.current) {
      const selectedIndex = wards.findIndex(
        (ward) => ward.objectid === selectedWard.objectid
      );
      
      if (selectedIndex !== -1) {
        // Scroll to the selected item with smooth animation
        virtualizer.scrollToIndex(selectedIndex, {
          align: 'center',
          behavior: 'smooth',
        });
      }
    }
  }, [selectedWard, wards, virtualizer]);

  return (
    <div ref={parentRef} className="h-full overflow-auto bg-gray-50">
      <div className="py-2" />
      <div
        className="relative w-full"
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const ward = wards[virtualItem.index];
          return (
            <div
              key={virtualItem.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <WardCard ward={ward} />
            </div>
          );
        })}
      </div>
      <div className="py-2" />
    </div>
  );
}
